import { globalEvent } from '@shared/easyfy.core';
import { hide, parseToBoolean, show, isMobile, addCss, removeCss, debounce } from '@shared/easyfy.utils';
import searchService from '@webServices/search.service';
import Kevel, { KevelContainerSelector } from '@clientWebModules/kevel';
import { HayppFeatureNames } from '@clientShared/HayppFeatureNames';
import { Features } from '@webServices/features.service';
export class SearchConfig {
  constructor(thresholdVolume: number = 3, displayStyle: string = 'block') {
    this.ThresholdVolume = thresholdVolume;
    this.DisplayStyle = displayStyle;
  }

  public ThresholdVolume: number;
  public DisplayStyle: string;
}

export default class Search {
  static Instance: Search;

  private backdrop: HTMLElement;
  private searchForm: HTMLElement;
  private searchInput: HTMLInputElement;
  private searchResults: HTMLElement;
  private searchBarElement: HTMLElement;
  private searchIcon: HTMLElement;
  private campaignResults: HTMLElement = null;
  private popularResults: HTMLElement = null;

  private pendingSearch: string;
  private focusInCampaignData: string = null;
  private focusInPopularData: string = null;

  private initialized: boolean;

  private thresholdVolume: number;
  private showPopularResultsOnFocusIn: boolean;
  private showCampaignsOnFocusIn: boolean;
  public keepPopularResultsOnSearch: boolean;
  public keepCampaignProductsOnSearch: boolean;
  public keepCampaignsOnSearch: boolean;
  public showPopularResultsIfNoResults: boolean;
  public displayStyle: string;
  public preventSubmit: boolean;

  private kevelProducts: HTMLElement = null;

  constructor(config: SearchConfig) {
    Search.Instance = this;

    if (config != null) {
      Search.Instance.thresholdVolume = config.ThresholdVolume;
      Search.Instance.displayStyle = config.DisplayStyle;
    }
  }

  OnInit() {
    globalEvent({
      type: 'focusin',
      event: {
        action: 'search-input',
        callback: Search.onSearch,
      },
    });

    globalEvent({
      type: 'keyup',
      event: {
        action: 'search-input',
        callback: debounce(Search.onSearch, 500),
      },
    });

    globalEvent({
      type: 'click',
      event: {
        event: 'search-close',
        callback: Search.CloseSearch,
      },
    });

    globalEvent({
      type: 'click',
      event: {
        action: 'search-suggestion',
        callback: Search.onSearchSuggestion,
      },
    });

    globalEvent({
      type: 'click',
      event: {
        action: 'search-open',
        callback: Search.onSearchOpen,
      },
    });
  }
  private static onSearchOpen = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // trigger click
    const el = e.target as HTMLElement;
    const fromEl = el?.parentElement as HTMLElement;
    const searchInputElement = fromEl?.querySelector('[data-action="search-input"]') as HTMLInputElement;

    if (!searchInputElement) return false;
    await Search.initNewSearchInstance(searchInputElement);
    Search.Instance.initialized = true;
    searchInputElement.focus();

    return false;
  };

  private static onSearch = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'focusin') {
      await Search.initNewSearchInstance(e.target as HTMLInputElement);
      Search.Instance.initialized = true;
    }

    if (e.key === 'Escape') {
      Search.CloseSearch(e);
    } else if (e.type === 'focusin' || e.type === 'keyup') {
      Search.Instance.ShowBackdrop(null);

      if (Search.Instance.searchIcon) show(Search.Instance.searchIcon);

      const value: string = Search.Instance.searchInput.value.trim() ?? '';

      //prevents multiple requests from different events for same query
      if (Search.Instance.pendingSearch !== value?.trim()) {
        Search.Instance.pendingSearch = value?.trim();

        const targetAttrs = e.target.attributes;
        const token = targetAttrs['data-token']?.value;

        if (Search.ShouldSearchBasedOnUserInput()) {
          await Search.GetSearchDataBasedOnUserInput(targetAttrs, token);

          if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = 'none';
          }

          if (!Search.Instance.keepCampaignProductsOnSearch && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = 'none';
          }
        } else if (Search.ShouldGetFocusInData()) {
          if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = 'none';
          }

          if (!Search.Instance.keepCampaignProductsOnSearch && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = 'none';
          }

          await Search.GetFocusInData(token);
        } else if (!Search.Instance.initialized) {
          Search.Instance.SearchClear();
        }

        if (Search.ShouldHideSearchBasedOnUserInput()) {
          Search.Instance.searchResults.style.display = 'none';
        }
      }
    }

    return false;
  };

  private static onSearchSuggestion = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const element: HTMLElement = e.target as HTMLElement;
    const newValue = element.attributes['data-value']?.value;

    Search.Instance.searchInput.value = newValue;
    Search.Instance.searchInput.focus();

    return false;
  };

  private static ShouldSearchBasedOnUserInput() {
    return Search.Instance.pendingSearch != null && Search.Instance.pendingSearch?.length >= Search.Instance.thresholdVolume;
  }

  private static ShouldHideSearchBasedOnUserInput() {
    return Search.Instance.pendingSearch != null && Search.Instance.pendingSearch?.length < Search.Instance.thresholdVolume;
  }

  private static ShouldGetFocusInData() {
    return (
      (Search.Instance.showPopularResultsOnFocusIn || Search.Instance.showCampaignsOnFocusIn) &&
      Search.Instance.pendingSearch != null &&
      Search.Instance.pendingSearch?.length < Search.Instance.thresholdVolume
    );
  }

  private static GetSearchDataBasedOnUserInput = async (targetAttrs: any, token: any) => {
    const element = document.querySelector('.subcription-interval .value[data-subscription-interval]');
    const interval = element ? element.getAttribute('data-subscription-interval') : '';
    const searchData = {
      includeArticles: parseToBoolean(targetAttrs['data-include-articles']?.value),
      includeBrands: parseToBoolean(targetAttrs['data-include-brands']?.value),
      includePages: parseToBoolean(targetAttrs['data-include-pages']?.value),
      includeProductSeries: parseToBoolean(targetAttrs['data-include-productseries']?.value),
      subscriptionProductsOnly: parseToBoolean(targetAttrs['data-query-subscriptions']?.value),
      searchTerm: Search.Instance.pendingSearch,
      subscriptionInterval: interval,
      preventSubmit: Search.Instance.preventSubmit,
    };

    try {
      const response = await searchService.Search(searchData, token);

      if (Search.Instance.pendingSearch == response.data.query) {
        Search.Instance.pendingSearch = null;
      }

      if (response.data.success) {
        // ignore result if input was emptied during fetch of data
        if (Search.Instance.searchInput.value.trim()) {
          Search.Instance.searchResults.innerHTML = response.data.partial;
          show(Search.Instance.searchResults, Search.Instance.displayStyle);

          var noResultsContainer = document.querySelector('[data-container="no-results"]');
          if (noResultsContainer != null && Search.Instance.showPopularResultsIfNoResults && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = Search.Instance.displayStyle;
          } else if (Search.Instance.popularResults != null) {
            Search.Instance.popularResults.style.display = 'none';
          }
        }

        await Search.Instance.displayKevelData();
      }
    } catch (error) {
      console.error('Error fetching search data:', error);
    }
  };

  private static GetFocusInData = async (token: any) => {
    // don't get focusIn data from server if we already have it in this instance
    if (Search.Instance.focusInCampaignData != null || Search.Instance.focusInPopularData != null) {
      Search.Instance.campaignResults != null ? (Search.Instance.campaignResults.innerHTML = Search.Instance.focusInCampaignData) : null;
      Search.Instance.campaignResults != null ? (Search.Instance.campaignResults.style.display = Search.Instance.displayStyle) : false;

      Search.Instance.popularResults != null ? (Search.Instance.popularResults.innerHTML = Search.Instance.focusInPopularData) : null;
      Search.Instance.popularResults != null ? (Search.Instance.popularResults.style.display = Search.Instance.displayStyle) : false;

      return;
    }

    const focusInParams = {
      GetPopularResults: Search.Instance.showPopularResultsOnFocusIn,
      GetCampaignResults: Search.Instance.showCampaignsOnFocusIn,
    };

    searchService.GetFocusInData(focusInParams, token).then((response) => {
      if (Search.Instance.pendingSearch == response.data.query) {
        Search.Instance.pendingSearch = null;
      }

      if (response.data.success) {
        Search.Instance.campaignResults.innerHTML = response.data.partialCampaignResults;
        Search.Instance.focusInCampaignData = response.data.partialCampaignResults;

        Search.Instance.popularResults.innerHTML = response.data.partialPopularResults;
        Search.Instance.focusInPopularData = response.data.partialPopularResults;

        if (Search.Instance.popularResults.innerHTML !== '') {
          Search.Instance.popularResults.style.display = Search.Instance.displayStyle;
        }

        if (Search.Instance.campaignResults.innerHTML !== '') {
          Search.Instance.campaignResults.style.display = Search.Instance.displayStyle;
        } else {
          Search.Instance.campaignResults.style.display = 'none';
        }
      }
    });
  };

  private static initNewSearchInstance = async (searchInputElement: HTMLInputElement) => {
    const searchBar = searchInputElement.closest('[data-container="search-bar"]');

    Search.Instance.searchBarElement = searchInputElement.closest('[data-container="search-bar"]');
    Search.Instance.searchBarElement.classList.add('active');
    Search.Instance.searchInput = searchInputElement;
    Search.Instance.searchResults = Search.Instance.searchBarElement.querySelector('[data-container="search-result"]') as HTMLElement;
    Search.Instance.campaignResults = Search.Instance.searchBarElement.querySelector('[data-container="campaign-results"]') as HTMLElement;
    Search.Instance.popularResults = Search.Instance.searchBarElement.querySelector('[data-container="popular-results"]') as HTMLElement;
    Search.Instance.backdrop = searchBar?.parentNode?.querySelector('[data-container="search-backdrop"]') as HTMLElement;
    Search.Instance.backdrop?.classList.add('active');
    Search.Instance.searchIcon = Search.Instance.searchBarElement.querySelector('[data-event="search-close"]') as HTMLElement;
    Search.Instance.searchForm = Search.Instance.searchBarElement.querySelector('form');
    Search.Instance.preventSubmit = parseToBoolean(searchInputElement.attributes['data-prevent-submit']?.value);
    Search.Instance.showPopularResultsOnFocusIn = parseToBoolean(searchInputElement.attributes['data-show-popular-results']?.value);
    Search.Instance.showCampaignsOnFocusIn = parseToBoolean(searchInputElement.attributes['data-show-campaigns']?.value);
    Search.Instance.keepPopularResultsOnSearch = parseToBoolean(searchInputElement.attributes['data-keep-popular-results']?.value);
    Search.Instance.keepCampaignsOnSearch = parseToBoolean(searchInputElement.attributes['data-keep-campaigns']?.value);
    Search.Instance.showPopularResultsIfNoResults = parseToBoolean(searchInputElement.attributes['data-show-popular-results-if-no-results']?.value);

    if (Search.Instance.backdrop != null && isMobile()) {
      document.body.style.overflow = 'hidden';
      addCss(document.body, 'noof');
    }

    if (Search.Instance.preventSubmit) Search.preventSubmit();

    if (!Search.Instance.preventSubmit) Search.preventEmptySearch();
    Search.hideSearchEventListener();
  };

  private static hideSearchEventListener = () => {
    const backdrop = Search.Instance.backdrop;
    backdrop?.addEventListener(
      'click',
      (event: any) => {
        if (
          !backdrop.contains(event.target) ||
          (event.target.hasAttribute('data-container') && event.target.attributes['data-container']?.value == 'search-backdrop')
        ) {
          Search.Instance.SearchClear();
          document.body.style.overflow = 'auto';
        }
      },
      { once: true }
    );
  };

  private static CloseSearch(e) {
    e.preventDefault();
    e.stopPropagation();

    Search.Instance.SearchClear();

    return false;
  }

  private static preventEmptySearch = () => {
    Search.Instance.searchForm?.addEventListener('submit', (e) => {
      if (!Search.Instance.searchInput.value) {
        e.preventDefault();
        return;
      }
    });
  };

  private static preventSubmit = () => {
    Search.Instance.searchForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      return;
    });
  };

  private displayKevelData = async () => {
    if (!Features.FeatureIsActive(HayppFeatureNames.Kevel)) return;

    const kevelContainer = document.querySelector(KevelContainerSelector.SearchFlyoutContainer) as HTMLElement;
    const kevelProductsContainer = document.querySelector(KevelContainerSelector.SearchFlyoutProducts) as HTMLElement;

    if (!kevelContainer || !kevelProductsContainer) return;

    if (Search.Instance.kevelProducts) {
      kevelProductsContainer.outerHTML = Search.Instance.kevelProducts.outerHTML;
      return;
    }

    const replacedKevelContent = await Kevel.Instance.replaceWithKevelData(true, true);
    const match = replacedKevelContent.find((c) => c === kevelProductsContainer);

    Search.Instance.kevelProducts = match || null;
    if (!match) kevelContainer.style.display = 'none';
  };

  SearchClear() {
    Search.Instance.HideSearchResult(null);
    Search.Instance.HideBackdrop(null);
    Search.Instance.searchInput.blur();
    Search.Instance.searchInput.value = '';

    if (document.body.style.overflow == 'hidden') document.body.style.overflow = 'auto';
    removeCss(document.body, 'noof');

    if (Search.Instance.searchIcon != null) {
      hide(Search.Instance.searchIcon);
    }
  }

  SearchBarInFocus(e) {
    if (e.type === 'focusin') {
      if (!Search.Instance.IsEventTargetInsideSearch(e) && Search.Instance.searchResults != null) {
        hide(Search.Instance.searchResults);
      }
    }
  }

  HideSearchResult(e: any) {
    e?.target ? (!Search.Instance.IsEventTargetInsideSearch(e) ? hide(Search.Instance.searchResults) : '') : hide(Search.Instance.searchResults);
    Search.Instance.searchBarElement.classList.remove('active');
  }

  ShowSearchResult(e: any) {
    e?.target
      ? !Search.Instance.IsEventTargetInsideSearch(e)
        ? show(Search.Instance.searchResults, Search.Instance.displayStyle)
        : ''
      : show(Search.Instance.searchResults, Search.Instance.displayStyle);
  }

  HideBackdrop(e: any) {
    e?.target
      ? !Search.Instance.IsEventTargetInsideSearch(e)
        ? Search.Instance.backdrop?.classList.add('active')
        : ''
      : Search.Instance.backdrop?.classList.remove('active');
  }

  ShowBackdrop(e: any) {
    if (Search.Instance.backdrop)
      e?.target
        ? !Search.Instance.IsEventTargetInsideSearch(e)
          ? Search.Instance.backdrop?.classList.remove('active')
          : ''
        : Search.Instance.backdrop?.classList.add('active');
  }

  IsEventTargetInsideSearch(e: any) {
    if (this.searchBarElement == null) {
      return false;
    }

    return this.searchBarElement.contains(e.target);
  }
}
